/**
 * Player settings — the one bridge between the Settings screen and the arena.
 *
 * localStorage, not the backend: every value here is a per-device presentation preference
 * (a mouse's sensitivity means nothing on another mouse), the arena must be able to read
 * them before it has any network, and none of them can affect the simulation — the server
 * never sees a sensitivity or a volume. Values that DO affect play (loadout, skin) live on
 * the server and never in this file.
 *
 * Reads are defensive to the point of paranoia because localStorage is user-editable state:
 * every field is clamped or checked against its legal values, and a corrupt blob degrades to
 * the defaults rather than throwing during arena boot — a broken settings store must never
 * cost a player the match they were joining.
 */

export interface PlayerSettings {
  /** Mouse look multiplier. 1 is the tuned default in `input.ts`. */
  sensitivity: number;
  /** Master volume, 0..1. Independent of the in-match mute toggle (M). */
  masterVolume: number;
  /** Menu ambient music. Master volume still scales it; this is the hard off switch. */
  musicEnabled: boolean;
  /** Post-processing (bloom) on or off. */
  postProcessing: boolean;
  /**
   * Ambient occlusion (GTAO) on or off. Its own switch because it is the one post pass with a
   * real per-frame cost; `null` means "the arena decides" — on for mouse-and-keyboard, off on a
   * touch device (`render-settings.ts` `AO`).
   */
  ambientOcclusion: boolean | null;
}

export const SETTINGS_DEFAULTS: PlayerSettings = {
  sensitivity: 1,
  masterVolume: 1,
  musicEnabled: true,
  postProcessing: true,
  ambientOcclusion: null,
};

export const SENSITIVITY_RANGE = { min: 0.2, max: 3 } as const;

const STORAGE_KEY = 'blackline:settings';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function loadSettings(storage: Pick<Storage, 'getItem'> = localStorage): PlayerSettings {
  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return { ...SETTINGS_DEFAULTS };
  }
  if (!raw) return { ...SETTINGS_DEFAULTS };

  try {
    const parsed = JSON.parse(raw) as Partial<PlayerSettings>;
    return {
      sensitivity:
        typeof parsed.sensitivity === 'number' && Number.isFinite(parsed.sensitivity)
          ? clamp(parsed.sensitivity, SENSITIVITY_RANGE.min, SENSITIVITY_RANGE.max)
          : SETTINGS_DEFAULTS.sensitivity,
      masterVolume:
        typeof parsed.masterVolume === 'number' && Number.isFinite(parsed.masterVolume)
          ? clamp(parsed.masterVolume, 0, 1)
          : SETTINGS_DEFAULTS.masterVolume,
      musicEnabled:
        typeof parsed.musicEnabled === 'boolean' ? parsed.musicEnabled : SETTINGS_DEFAULTS.musicEnabled,
      postProcessing:
        typeof parsed.postProcessing === 'boolean'
          ? parsed.postProcessing
          : SETTINGS_DEFAULTS.postProcessing,
      ambientOcclusion: typeof parsed.ambientOcclusion === 'boolean' ? parsed.ambientOcclusion : null,
    };
  } catch {
    return { ...SETTINGS_DEFAULTS };
  }
}

export function saveSettings(
  update: Partial<PlayerSettings>,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): PlayerSettings {
  const merged = { ...loadSettings(storage), ...update };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Quota or privacy mode — the setting still applies for this session via the return value.
  }
  // Same-tab listeners (the menu music player, live volume consumers) — the `storage` event
  // only fires in OTHER tabs, so a change made on the Settings screen needs its own signal.
  try {
    window.dispatchEvent(new CustomEvent('bl:settings-changed', { detail: merged }));
  } catch {
    /* jsdom without CustomEvent config, or a worker context — listeners just poll on resume */
  }
  return merged;
}
